import { faFilePdf } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  AccordionDetails,
  AccordionSummary,
  Accordion,
  Typography,
  Stack,
  Button,
  Divider,
} from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import FileDownload from "js-file-download";
import { useParams } from "react-router-dom";

import { UserUploadedFile } from "api/memberPortal/files/files.types";
import {
  useLazyPublicFileQuery,
  usePclFilesQuery,
} from "api/public/file/file.api";
import { PclFileResponse } from "api/public/file/file.types";
import { useLoading } from "components/UI/LoadingSpinner/UseLoading";
import amFileIcon from "images/assurance-maintenance-file-icon.svg";

const EMPTY_RESPONSE = {
  am_files: [],
  eval_files: [],
} as PclFileResponse;

const DocumentsSection = () => {
  const { showLoadingSpinner, hideLoadingSpinner } = useLoading();

  const { id } = useParams();

  const { data: files = EMPTY_RESPONSE } = usePclFilesQuery(id ?? skipToken);

  const [triggerFileDownload] = useLazyPublicFileQuery();
  const downloadFile = async (file: UserUploadedFile) => {
    showLoadingSpinner("Downloading file...");

    triggerFileDownload(file.file_id)
      .unwrap()
      .then((data) => {
        FileDownload(data, file.file_new_name ?? file.file_name);
      })
      .finally(() => {
        hideLoadingSpinner();
      });
  };

  return (
    <>
      <Accordion
        sx={{
          backgroundColor: "primary.main",
          color: "primary.contrastText",
          px: 0,
          mx: 0,
          pt: 3,
        }}
        defaultExpanded
        disableGutters
      >
        <AccordionSummary
          sx={{
            px: 0,
          }}
          expandIcon={<ExpandMoreIcon sx={{ color: "primary.contrastText" }} />}
        >
          <Typography
            component="small"
            fontWeight="bold"
            fontSize=".875em"
            mb={1}
          >
            Documents
          </Typography>
        </AccordionSummary>

        {/* Product Files */}
        <AccordionDetails sx={{ px: 0, my: 0 }}>
          <Stack alignItems="flex-start">
            {files.eval_files.map((file) => {
              return (
                <FileItem
                  key={file.file_id}
                  file={file}
                  type="product"
                  onDownload={() => downloadFile(file)}
                />
              );
            })}
          </Stack>

          {/* AM Files */}
          <Stack alignItems="flex-start">
            {files.am_files.map((file) => {
              return (
                <FileItem
                  key={file.file_id}
                  file={file}
                  type="am"
                  onDownload={() => downloadFile(file)}
                />
              );
            })}
          </Stack>
        </AccordionDetails>
      </Accordion>
      <Divider
        sx={{
          opacity: 1,
          backgroundColor: "primary.contrastText",
          marginTop: 3,
        }}
      />
    </>
  );
};
export default DocumentsSection;

interface FileItemProps {
  file: UserUploadedFile;
  onDownload: () => void;
  type: "product" | "am";
}

// TODO: Consider splitting this out into its own file for reuse among the other file consumers on the site
const FileItem = ({ file, onDownload, type }: FileItemProps) => {
  return (
    <Button
      sx={{
        color: "primary.contrastText",
        textTransform: "none",
        my: 0.25,
      }}
      onClick={onDownload}
    >
      {type === "am" && <img src={amFileIcon} style={{ width: "22px" }} />}
      {type === "product" && (
        <FontAwesomeIcon
          icon={faFilePdf}
          color="red"
          size="2x"
          style={{ maxWidth: "25px" }}
        />
      )}
      <Typography fontSize="12px" ml={1} textAlign="left">
        {file.file_label}
      </Typography>
    </Button>
  );
};
